// useWebSocket.ts
import { useEffect, useRef, useCallback } from "react";

interface UseWebSocketProps {
  url: string;
  onMessage: (data: any) => void;
  onError?: (event: Event) => void;
  onClose?: (event: CloseEvent) => void;
  onOpen?: (event: Event) => void;
  enabled?: boolean;
}

const useWebSocket = ({
  url,
  onMessage,
  onError,
  onClose,
  onOpen,
  enabled = true,
}: UseWebSocketProps) => {
  const socket = useRef<WebSocket | null>(null);
  const onMessageRef = useRef(onMessage);
  const onErrorRef = useRef(onError);
  const onCloseRef = useRef(onClose);
  const onOpenRef = useRef(onOpen);

  useEffect(() => {
    onMessageRef.current = onMessage;
  }, [onMessage]);

  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    onOpenRef.current = onOpen;
  }, [onOpen]);

  useEffect(() => {
    if (!enabled) {
      return;
    }
    // Crear una nueva instancia de WebSocket
    socket.current = new WebSocket(url);

    // Evento cuando la conexión se establece
    socket.current.onopen = (event: Event) => {
      if (onOpenRef.current) onOpenRef.current(event);
    };

    // Evento cuando se recibe un mensaje
    socket.current.onmessage = (event: MessageEvent) => {
      try {
        const data = JSON.parse(event.data);
        onMessageRef.current(data);
      } catch (error) {
        console.error("Error al parsear el mensaje:", error);
      }
    };

    // Evento cuando ocurre un error
    socket.current.onerror = (event: Event) => {
      console.error("Error en la conexión WebSocket:", event);
      if (onErrorRef.current) onErrorRef.current(event);
    };

    // Evento cuando la conexión se cierra
    socket.current.onclose = (event: CloseEvent) => {
      // console.log("Conexión WebSocket cerrada:", event.reason);
      if (onCloseRef.current) onCloseRef.current(event);
    };

    // Limpiar al desmontar el componente
    return () => {
      if (socket.current) {
        socket.current.close();
      }
    };
  }, [enabled, url]);

  // Función para enviar mensajes a través del WebSocket
  const sendMessage = useCallback((message: any) => {
    if (socket.current && socket.current.readyState === WebSocket.OPEN) {
      socket.current.send(JSON.stringify(message));
    } else {
      console.error(
        "No se puede enviar el mensaje, el WebSocket no está conectado"
      );
    }
  }, []);

  return { sendMessage };
};

export default useWebSocket;
